import { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../modules/auth/context/AuthContext';
import { useIncomingCalls } from './IncomingCallProvider';
import { formatCallerNumber } from '../../modules/telephony/utils/callFormat';

const ring = keyframes`
  0%, 100% { transform: rotate(0); }
  20% { transform: rotate(-18deg); }
  40% { transform: rotate(14deg); }
  60% { transform: rotate(-10deg); }
  80% { transform: rotate(6deg); }
`;

const Wrap = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: 14px;
  padding: 6px 14px;
  border: 0;
  border-radius: 18px;
  background: ${(p) => (p.$mode === 'talking' ? 'var(--spark-color-success-surface)' : p.$mode === 'ringing' ? 'var(--spark-color-warning-surface)' : 'transparent')};
  color: ${(p) => (p.$mode === 'talking' ? 'var(--spark-color-success-text)' : p.$mode === 'ringing' ? 'var(--spark-color-warning-text)' : 'var(--spark-color-text-secondary)')};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const Phone = styled.span`
  display: inline-block;
  font-size: 16px;
  line-height: 1;
  filter: ${(p) => (p.$mode === 'idle' || p.$mode === 'off' ? 'var(--spark-color-muted-icon-filter)' : 'none')};
  ${(p) => p.$mode === 'ringing' && css`animation: ${ring} 0.7s ease-in-out infinite;`}
`;

const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => (p.$ok ? 'var(--spark-color-success-text)' : p.$off ? 'var(--spark-color-control-neutral)' : 'var(--spark-color-error-text)')};
  box-shadow: 0 0 0 3px ${(p) => (p.$ok ? 'var(--spark-color-success-surface)' : p.$off ? 'transparent' : 'var(--spark-color-error-ring)')};
`;

function fmt(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Індикатор телефонії в шапці (лише для receive_calls):
 *  - точка: зелена — SSE і PBX підключені, червона — щось відвалилось,
 *    сіра — інтеграцію вимкнено в адмінці / не налаштовано;
 *  - трубка: сіра — тихо, хитається помаранчевим — дзвонить на ваш номер,
 *    зелена з таймером — ви розмовляєте.
 * Клік під час дзвінка/розмови відкриває сторінку дзвінка.
 */

/** Причина відсутності SSE-потоку — з probe у useSseEvents; без деталей — загальний текст. */
function sseReason(err) {
  if (err?.status === 403) return 'Сервер відхилив підписку на події (403): право receive_calls не дає доступу — перевірте налаштування права';
  if (err?.status === 401) return 'Сесія завершилась — увійдіть знову';
  if (err?.reachable) return 'Сервер SPARK відповідає, але потік подій (SSE) не тримається — проксі чи мережа розривають довге з\'єднання';
  if (err && err.status === 0) return 'Сервер SPARK недоступний — перевірте мережу';
  return 'Немає зв\'язку з сервером SPARK (SSE)';
}
export default function PbxStatusIndicator() {
  const { enabled, pbxStatus, sseConnected, sseError, calls } = useIncomingCalls();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());

  const talking = calls.find((c) => c.status === 'ANSWERED' && c.answeredUserId === user?.id);
  const ringing = !talking && calls.find((c) => c.status === 'RINGING');

  useEffect(() => {
    if (!talking) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [talking]);

  if (!enabled) return null;

  const off = pbxStatus && (pbxStatus.enabled === false || pbxStatus.configured === false);
  const ok = sseConnected && pbxStatus?.connected;
  const dotTitle = off
    ? (pbxStatus.configured === false ? 'Телефонія не налаштована на сервері (AMI_HOST)' : 'Телефонію вимкнено в налаштуваннях')
    : !sseConnected
      ? sseReason(sseError)
      : pbxStatus?.connected
        ? `PBX підключена (${pbxStatus.host})`
        : 'Сервер не підключений до PBX';

  const mode = off ? 'off' : talking ? 'talking' : ringing ? 'ringing' : 'idle';
  const current = talking || ringing;
  const label = talking
    ? `${formatCallerNumber(talking.callerNumberRaw)} · ${fmt(now - new Date(talking.answeredAt).getTime())}`
    : ringing
      ? `${formatCallerNumber(ringing.callerNumberRaw)} · дзвонить`
      : null;

  return (
    <Wrap
      type="button"
      $mode={mode}
      $clickable={Boolean(current)}
      onClick={current ? () => navigate(`/telephony/calls/${current.callId}`) : undefined}
      title={`${dotTitle}${label ? ` · ${label}` : ''}`}
      aria-label={dotTitle}
    >
      <Dot $ok={ok && !off} $off={off} />
      <Phone $mode={mode} aria-hidden="true">📞</Phone>
      {label && <span>{label}</span>}
    </Wrap>
  );
}
